import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

/**
 * CSS injected into the bundle document to hide Writerside's own chrome, so the
 * app's navigation is the only navigation on screen.
 *
 * This targets generated markup and is the most version-sensitive piece of the
 * integration. If a Writerside upgrade changes these class names the frame
 * degrades to showing the bundle's own sidebar — visually wrong but still
 * readable — rather than breaking.
 */
const SUPPRESS_CHROME = `
  header, .toc, .navigation-links, [class*="sidebar"], [class*="TableOfContents"],
  [data-test="toc"], nav[aria-label], .product-switcher, .header, footer { display: none !important; }
  main, article, .article, .wrapper, .content { margin: 0 !important; padding: 0 !important; max-width: none !important; width: auto !important; }
  body { overflow-x: hidden; }
`;

interface Props {
  /** Bundle-relative URL, e.g. /docs/intern/isms-leitlinie.html */
  src: string;
  /** Called when the frame navigates to another page of the same bundle. */
  onNavigate: (slug: string) => void;
}

export function DocFrame({ src, onNavigate }: Props) {
  const { t } = useTranslation();
  const ref = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    const frame = ref.current;
    if (!frame) return;

    const handleLoad = (): void => {
      let doc: Document | null = null;
      try {
        doc = frame.contentDocument;
      } catch {
        return; // Not same-origin after all; leave the bundle as it renders.
      }
      if (!doc) return;

      const style = doc.createElement("style");
      style.textContent = SUPPRESS_CHROME;
      doc.head.appendChild(style);

      // Writerside pages cross-reference each other. Those clicks navigate
      // inside the frame, which would leave the app's route and metadata band
      // showing the wrong document — so map the href back to a slug and tell
      // the page to catch up.
      const path = frame.contentWindow?.location.pathname ?? "";
      const file = path.split("/").pop() ?? "";
      if (file.endsWith(".html")) {
        const slug = file === "index_md.html" ? "index" : file.slice(0, -5);
        onNavigate(slug);
      }
    };

    frame.addEventListener("load", handleLoad);
    return () => frame.removeEventListener("load", handleLoad);
  }, [onNavigate]);

  return (
    <iframe
      ref={ref}
      src={src}
      title={t("docs.frame.title")}
      style={{ width: "100%", height: "100%", border: 0, display: "block" }}
    />
  );
}
